import { PageHeader } from "@/components/page-header";
import { useCurrentProject } from "@/features/projects/current-project";
import { mapApiError } from "@/lib/errors";
import { type Family, familyOf } from "@/lib/family";
import { type HistoryEntry, clearHistory, loadHistory, pushHistory } from "@/lib/history";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useModels, useRevisions } from "../models/queries";
import { callPredict } from "./mutations";
import type { ModelSchema } from "./queries";
import { useModelSchema } from "./queries";
import { RequestPanel } from "./request-panel";
import { ResponsePanel } from "./response-panel";
import { TargetBar } from "./target-bar";

export function InferencePage() {
  const { projectId } = useCurrentProject();
  const navigate = useNavigate();
  const search = useSearch({ from: "/inference" });
  const modelParam = (search as { model?: string }).model ?? null;

  const { data: models } = useModels(projectId);

  const [modelId, setModelId] = useState<string | null>(null);
  const [revisionNo, setRevisionNo] = useState<number | null>(null);
  const [apiKeyId, setApiKeyId] = useState<string | null>(null);
  const [secret, setSecret] = useState<string>("");

  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [lastResult, setLastResult] = useState<HistoryEntry | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: revisions } = useRevisions(modelId ?? "");
  const { data: schema } = useModelSchema(modelId);
  const model = models?.items.find((m) => m.id === modelId) ?? null;
  const family: Family | null = familyOf(model?.task_type ?? null);

  // Seed model from search param or first available model
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!models?.items.length) return;
    seededRef.current = true;

    const target = modelParam ? models.items.find((m) => m.id === modelParam) : models.items[0];
    if (target) {
      setModelId(target.id);
      // revision defaults via revisions-loaded effect below (picks active rev)
    }
    // Update URL if it doesn't match
    if (target && (!modelParam || modelParam !== target.id)) {
      void navigate({ to: "/inference", search: { model: target.id } });
    }
  }, [models, modelParam, navigate]);

  // Seed revision from active revision when model changes
  const handleModelChange = useCallback(
    (id: string) => {
      setModelId(id);
      setRevisionNo(null);
      void navigate({ to: "/inference", search: { model: id } });
    },
    [navigate],
  );

  // Update revision default when revisions load
  useEffect(() => {
    if (revisionNo != null) return;
    if (!revisions?.items.length) return;
    const active = revisions.items.find((r) => r.status === "active");
    if (active) setRevisionNo(active.revision_no);
    else setRevisionNo(revisions.items[0]?.revision_no ?? null);
  }, [revisions, revisionNo]);

  // No auto-seed: user must explicitly pick an API key for which they have
  // the secret saved in this session (or paste one in the Request panel).

  // Load per-key-id secret from sessionStorage when apiKeyId changes
  useEffect(() => {
    if (apiKeyId) setSecret(sessionStorage.getItem(`autolab:secret:${apiKeyId}`) ?? "");
    else setSecret("");
  }, [apiKeyId]);

  // Persist secret to per-key-id sessionStorage slot
  const handleSecretChange = (s: string) => {
    setSecret(s);
    if (apiKeyId) {
      try {
        sessionStorage.setItem(`autolab:secret:${apiKeyId}`, s);
      } catch {
        // ignore
      }
    }
  };

  // Load history when model/project changes
  useEffect(() => {
    if (!projectId || !modelId) return;
    setHistory(loadHistory(projectId, modelId));
  }, [projectId, modelId]);

  const handleSubmit = useCallback(
    async ({ kind, inputs }: { kind: "single" | "batch"; inputs: unknown }) => {
      if (!modelId) {
        toast.error("Select a model first");
        return;
      }
      if (!family) {
        toast.error("Unsupported or unknown model task type");
        return;
      }
      if (revisionNo == null) {
        toast.error("Select a revision");
        return;
      }

      let body: Record<string, unknown>;
      if (family === "forecast") {
        if (!inputs || typeof inputs !== "object" || Array.isArray(inputs)) {
          toast.error("Forecast body must be a JSON object");
          return;
        }
        body = { revision_no: revisionNo, ...(inputs as Record<string, unknown>) };
      } else {
        if (kind === "batch") {
          const arr = inputs as unknown[];
          if (!Array.isArray(arr)) {
            toast.error("Batch input must be an array");
            return;
          }
          if (arr.length > 100) {
            toast.error("Batch capped at 100 rows (inline predict)");
            return;
          }
          body = { revision_no: revisionNo, inputs: arr };
        } else {
          body = { revision_no: revisionNo, inputs };
        }
      }

      setBusy(true);
      try {
        const result = await callPredict(modelId, family, body, secret);
        const responseObj =
          result.body && typeof result.body === "object"
            ? (result.body as Record<string, unknown>)
            : null;
        const entry: HistoryEntry = {
          ts: Date.now(),
          input: inputs,
          status: result.status,
          latency_ms: result.latency_ms,
          response: result.body,
          headers: result.headers,
          warnings:
            (responseObj?.warnings as Array<Record<string, unknown>> | undefined) ?? undefined,
          revision_used:
            (responseObj?.revision_used as { revision_no: number; revision_id: string } | null) ??
            null,
          threshold_used:
            typeof responseObj?.threshold_used === "number" ? responseObj.threshold_used : null,
          revision_no: revisionNo ?? undefined,
        };
        if (result.status >= 400) {
          // surface the error via the error map (used for toast copy)
          const code =
            (typeof responseObj?.error === "object" && responseObj?.error !== null
              ? ((responseObj.error as { code?: string }).code ?? "")
              : "") || `${result.status}`;
          const mapped = mapApiError(result.status, code, responseObj ?? {});
          entry.error = mapped.message;
          // 404 with available_revisions → richer hint
          if (mapped.availableRevisions && mapped.availableRevisions.length > 0) {
            const nos = mapped.availableRevisions.map((r) => r.no).join(", ");
            entry.error = `${mapped.message} (available: ${nos})`;
          }
          toast.error(`Request failed: ${entry.error}`);
        } else {
          toast.success(`Prediction complete · ${result.latency_ms} ms`);
        }
        setLastResult(entry);
        if (projectId) {
          pushHistory(projectId, modelId, entry);
          setHistory(loadHistory(projectId, modelId));
        }
      } catch (e) {
        const entry: Omit<HistoryEntry, "ts"> = {
          input: inputs,
          status: 0,
          latency_ms: 0,
          response: null,
          error: (e as Error).message,
          revision_no: revisionNo ?? undefined,
        };
        if (projectId) {
          pushHistory(projectId, modelId, entry);
          setHistory(loadHistory(projectId, modelId));
        }
        setLastResult({ ...entry, ts: Date.now() });
        toast.error(`Request failed: ${(e as Error).message}`);
      } finally {
        setBusy(false);
      }
    },
    [modelId, family, revisionNo, secret, projectId],
  );

  const handleClearHistory = () => {
    if (!projectId || !modelId) return;
    clearHistory(projectId, modelId);
    setHistory([]);
    setLastResult(null);
  };

  const handleRestore = (h: HistoryEntry) => {
    if (h.input && typeof h.input === "object" && !Array.isArray(h.input)) {
      window.dispatchEvent(new CustomEvent("inference:restore", { detail: h.input }));
    }
    setLastResult(h);
  };

  const resolvedSchema: ModelSchema | null = schema ?? null;

  return (
    <>
      <PageHeader
        title="Inference Playground"
        subtitle="Send predictions against a live revision"
      />

      {/* Target bar */}
      <div className="mb-5">
        <TargetBar
          modelId={modelId}
          revisionNo={revisionNo}
          apiKeyId={apiKeyId}
          onModelChange={handleModelChange}
          onRevisionChange={setRevisionNo}
          onApiKeyChange={setApiKeyId}
        />
      </div>

      {/* Split layout */}
      <div className="grid grid-cols-2 gap-5">
        <RequestPanel
          modelId={modelId}
          revisionNo={revisionNo}
          family={family}
          schema={resolvedSchema}
          secret={secret}
          onSecretChange={handleSecretChange}
          onSubmit={handleSubmit}
          busy={busy}
        />
        <ResponsePanel
          result={lastResult}
          family={family}
          history={history}
          onRestore={handleRestore}
          onClear={handleClearHistory}
        />
      </div>
    </>
  );
}
